"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Scan, Compass, ArrowRight, CircleCheck as CheckCircle2, FileCheck2, Cpu } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const matrixCards = [{
  title: "Law Enforcement & Public Safety",
  description: "Field-deployable Raman spectrometers and optical analyzers for rapid, non-destructive identification of unknown substances, narcotics, and hazardous chemical threats.",
  href: "/LawEnforcementPublicSafetyPage",
  icon: ShieldCheck,
  tag: "Field Screening",
  details: "Point-of-need identification without container breach."
}, {
  title: "Border Security & Defence",
  description: "Ruggedized spectroscopic hardware and tactical software libraries designed for customs checkpoints, military screening, and border inspection operations.",
  href: "/BorderSecurityAndDefencePage",
  icon: Scan,
  tag: "Tactical Verification",
  details: "Comprehensive library updates and traceable reporting."
}, {
  title: "Safety & Security Overview",
  description: "Complete platform overview of analytical instrumentation, certified training, preventive maintenance, and lifecycle support across Ireland and the UK.",
  href: "/SafetyAndSecurityPage",
  icon: Compass,
  tag: "Family Overview",
  details: "End-to-end qualification and technical maintenance."
}];
const capabilities = [{
  label: "Non-destructive identification through clear packaging"
}, {
  label: "Traceable calibration and regulatory qualification"
}, {
  label: "Direct application training for front-line operational personnel"
}, {
  label: "Regional field servicing and dedicated technical support"
}];
export default function SafetyEcosystem() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="safetysecurityecosystem" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Narrative Split: 8 / 4 Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Narrative Column (8 Cols) */}
          <motion.div className="lg:col-span-8 flex flex-col items-start" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={containerVariants}>
            <motion.div variants={itemVariants}>
              <Badge variant="secondary" className="mb-4">
                Operational Ecosystem
              </Badge>
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default">
              Analytical certainty for mission-critical inspection and safety
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
              Antech Solutions provides certified analytical technology,
              application-led method development, and ongoing lifecycle
              qualification for regulated security environments. Front-line
              personnel depend on accurate chemical identification to make
              immediate decisions in high-consequence scenarios.
            </motion.p>

            <motion.p variants={itemVariants} className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed font-sans">
              Our instruments deliver rapid spectral matching against verified
              reference databases for narcotics, toxic industrial chemicals,
              and precursor compounds without risking sample contamination or
              operator exposure.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
              {capabilities.map((cap, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{cap.label}</span>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Right Summary Card (4 Cols) */}
          <motion.div className="lg:col-span-4 w-full" initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.45
        }}>
            <div className="rounded-xl border border-border bg-secondary/40 p-6 flex flex-col gap-5 shadow-xs">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-background border border-border text-primary shadow-2xs">
                  <Cpu className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground font-default">
                    Deployment Readiness
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    UK &amp; Ireland Support Coverage
                  </p>
                </div>
              </div>

              <Separator className="bg-border" />

              <div className="space-y-3 text-xs text-muted-foreground leading-relaxed">
                <div className="flex items-start gap-2">
                  <FileCheck2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <span>
                    Fully traceable calibration records and factory compliance
                    documentation supplied with all handheld systems.
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldCheck className="size-4 text-foreground shrink-0 mt-0.5" />
                  <span>
                    Guaranteed rapid turnaround for preventative maintenance,
                    spectral library updates, and technical repairs.
                  </span>
                </div>
              </div>

              <div className="pt-2">
                <Button asChild variant="outline" size="sm" className="w-full justify-between">
                  <Link to="/ContactPage">
                    <span>Inquire About Equipment</span>
                    <ArrowRight className="size-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* 3-Card Matrix */}
        <div className="mt-14 md:mt-20">
          <div className="mb-8">
            <h3 className="text-xl md:text-2xl font-semibold text-foreground font-default">
              Safety &amp; Security Sectors
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Select an area to explore specific analytical instruments,
              applications, and operational methodologies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {matrixCards.map((card, i) => {
            const IconComponent = card.icon;
            return <div key={card.title} data-index={i} className="h-full">
                  <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color]">
                    <CardHeader className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="p-2.5 rounded-lg bg-secondary text-foreground border border-border/80">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {card.tag}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground font-default pt-1">
                        {card.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground line-clamp-3">
                        {card.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="text-xs text-muted-foreground border-t border-border pt-3">
                        {card.details}
                      </p>
                    </CardContent>

                    <CardFooter className="pt-0">
                      <Button asChild variant="ghost" className="w-full justify-between px-2 text-foreground hover:text-primary">
                        <Link to={card.href}>
                          <span>View Sector Details</span>
                          <ArrowRight className="size-4 ml-2" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}